import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly INACTIVITY_TIME = 30 * 60 * 1000; // 30 minutos en milisegundos
  private readonly EXTEND_SESSION_TIME = 5 * 60 * 1000; // 5 minutos en milisegundos
  private inactivityTimer: any;
  private lastActivity: number = Date.now();
  private showPrompt = new BehaviorSubject<boolean>(false);
  showPrompt$ = this.showPrompt.asObservable();

  constructor(private router: Router, private authService: AuthService) {
    if (this.authService.isAuthenticated()) {
      this.initializeInactivityTimer();
    }

    this.router.events.subscribe(() => {
      if (this.authService.isAuthenticated()) {
        this.initializeInactivityTimer();
      } else {
        this.clearInactivityTimer();
      }
    });
  }

  // -------------------- Endpoints de Gestión de Inactividad --------------------
  /**
   * Extiende la sesión actual del usuario.
   * Reinicia el temporizador de inactividad y oculta el prompt de advertencia.
   */
  extendSession() {
    if (this.authService.isAuthenticated()) {
      this.showPrompt.next(false);
      this.lastActivity = Date.now();
      this.resetTimer();
    }
  }

  // -------------------- Métodos Privados --------------------
  private initializeInactivityTimer() {
    this.resetTimer();

    if (this.authService.isAuthenticated()) {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      events.forEach((event) => {
        document.addEventListener(event, (e: Event) => {
          if (e.target instanceof Element) {
            const isInModal = e.target.closest('.inactivity-modal');
            if (!isInModal) {
              this.userActivity();
            }
          } else {
            this.userActivity();
          }
        });
      });
    }
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.showPrompt.next(false);

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      document.removeEventListener(event, () => this.userActivity());
    });
  }

  private userActivity() {
    if (this.authService.isAuthenticated() && !this.showPrompt.value) {
      this.lastActivity = Date.now();
      this.resetTimer();
    }
  }

  private resetTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    if (this.authService.isAuthenticated()) {
      this.inactivityTimer = setTimeout(() => {
        const currentTime = Date.now();
        const timeSinceLastActivity = currentTime - this.lastActivity;

        if (timeSinceLastActivity >= this.INACTIVITY_TIME) {
          this.showPrompt.next(true);

          setTimeout(() => {
            if (this.showPrompt.value && this.authService.isAuthenticated()) {
              this.showPrompt.next(false);
              this.authService.signOut();
            }
          }, this.EXTEND_SESSION_TIME);
        }
      }, this.INACTIVITY_TIME);
    }
  }
}
